import { useQueries } from "@tanstack/react-query";
import { queryList } from "../../utils";
import {
  bookkeepingYearsByBookkeepingId,
  transactionsByBookkeepingYearId,
} from "../../graphql/queries";
import {
  BookkeepingYearsByBookkeepingIdQuery,
  BookkeepingYearsByBookkeepingIdQueryVariables,
  TransactionsByBookkeepingYearIdQuery,
  TransactionsByBookkeepingYearIdQueryVariables,
} from "../../API";
import {
  Bookkeeping,
  BookkeepingYear,
  Transaction,
} from "../../models";
import { approvalKeys } from "./query-keys";
import { isInboxStatus } from "../../utils/approval-status-helpers";

// Org-wide pending approval count per D-15 / D-31. Sums pending approvals
// across ALL bookkeeping-years of ALL bookkeepings in the org via TanStack
// `useQueries` parallelism. Shares the `approvalKeys.inbox` cache key with the
// year-scoped `useApprovalInbox`, so TanStack Query dedupes fetches when both
// are mounted. The year-scoped `usePendingApprovalCount` (nav badge) is left
// untouched.
export const useOrgPendingApprovalCount = (
  bookkeepings: Bookkeeping[] | undefined
) => {
  const yearQueries = useQueries({
    queries: (bookkeepings ?? []).map((bookkeeping) => ({
      queryKey: approvalKeys.orgYears(bookkeeping.id),
      queryFn: () =>
        queryList<
          BookkeepingYear,
          BookkeepingYearsByBookkeepingIdQueryVariables,
          BookkeepingYearsByBookkeepingIdQuery
        >(bookkeepingYearsByBookkeepingId, {
          bookkeepingId: bookkeeping.id,
        }),
      enabled: !!bookkeeping.id,
    })),
  });

  const allYearIds = yearQueries
    .flatMap((q) => q.data ?? [])
    .filter((by) => !by.deletedAt)
    .map((by) => by.id);

  const inboxQueries = useQueries({
    queries: allYearIds.map((yearId) => ({
      queryKey: approvalKeys.inbox(yearId),
      queryFn: async () => {
        const transactions = await queryList<
          Transaction,
          TransactionsByBookkeepingYearIdQueryVariables,
          TransactionsByBookkeepingYearIdQuery
        >(transactionsByBookkeepingYearId, {
          bookkeepingYearId: yearId,
        });
        return transactions.filter((transaction) =>
          isInboxStatus(transaction.approvalStatus)
        );
      },
      enabled: !!yearId,
    })),
  });

  const isLoading =
    yearQueries.some((q) => q.isLoading) ||
    inboxQueries.some((q) => q.isLoading);
  const count = inboxQueries.reduce(
    (sum, q) => sum + (q.data?.length ?? 0),
    0
  );

  return { count, isLoading };
};
